import { PlaybookDoc, PlaybookLang, PlaybookParityResult, PlaybookSection } from './types';

const HEADING_REGEX = /^(#{2,4})\s+\[([a-z0-9][a-z0-9#-]*)\]\s+(.+?)\s*$/;
const COMMENT_LINE_REGEX = /^\s*<!--.*-->\s*$/;
const FENCE_REGEX = /^\s*```/;
const HR_REGEX = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
/** `[id]` written inside text, but not an ordinary markdown link `[text](url)`. */
export const SECTION_LINK_REGEX = /\[([a-z0-9][a-z0-9-]*(?:#[a-z0-9-]+)?)\](?!\()/g;

/** Drops leading and trailing blank lines and a trailing `---` separator. */
const tidyBody = (lines: string[]): string => {
  const out = [...lines];
  while (out.length && (!out[out.length - 1].trim() || HR_REGEX.test(out[out.length - 1]))) out.pop();
  while (out.length && !out[0].trim()) out.shift();
  return out.join('\n');
};

/**
 * Parses a playbook markdown file into a flat, ordered list of sections.
 * Everything before the first `## [id]` heading (file title, `>` info block) is ignored.
 */
export const parsePlaybook = (markdown: string, lang: PlaybookLang): PlaybookDoc => {
  const sections: PlaybookSection[] = [];
  const byId: Record<string, PlaybookSection> = {};
  const stack: PlaybookSection[] = [];

  let current: { section: PlaybookSection; lines: string[] } | null = null;
  let inFence = false;

  const flush = () => {
    if (!current) return;
    current.section.body = tidyBody(current.lines);
    current = null;
  };

  markdown.split(/\r?\n/).forEach((line) => {
    if (FENCE_REGEX.test(line)) inFence = !inFence;
    const match = inFence ? null : line.match(HEADING_REGEX);

    if (match) {
      flush();
      const level = match[1].length as 2 | 3 | 4;
      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      const parent = stack[stack.length - 1];
      const id = match[2];
      const section: PlaybookSection = {
        id,
        level,
        title: match[3],
        body: '',
        parentId: parent?.id,
        rootId: parent ? parent.rootId : id,
      };
      stack.push(section);
      sections.push(section);
      byId[id] = section;
      current = { section, lines: [] };
      return;
    }

    if (!current) return;
    if (!inFence && COMMENT_LINE_REGEX.test(line)) return;
    current.lines.push(line);
  });
  flush();

  return { lang, sections, byId };
};

/** Ids referenced as `[id]` in a section body (code spans excluded). */
export const extractSectionLinks = (body: string): string[] => {
  const found: string[] = [];
  body.split(/(`[^`\n]*`)/).forEach((part, index) => {
    if (index % 2 === 1) return; // inside inline code
    part.replace(SECTION_LINK_REGEX, (_, id: string) => {
      found.push(id);
      return '';
    });
  });
  return found;
};

/** Compares the VI and EN files: both must hold the same ids in the same order, and every `[id]` link must resolve. */
export const validateParity = (vi: PlaybookDoc, en: PlaybookDoc): PlaybookParityResult => {
  const viIds = vi.sections.map((s) => s.id);
  const enIds = en.sections.map((s) => s.id);
  const enSet = new Set(enIds);
  const viSet = new Set(viIds);

  const missingInEn = viIds.filter((id) => !enSet.has(id));
  const missingInVi = enIds.filter((id) => !viSet.has(id));
  const sharedVi = viIds.filter((id) => enSet.has(id));
  const sharedEn = enIds.filter((id) => viSet.has(id));
  const orderMismatch = sharedVi.some((id, i) => id !== sharedEn[i]);

  const unknownLinks: PlaybookParityResult['unknownLinks'] = [];
  [vi, en].forEach((doc) => {
    doc.sections.forEach((section) => {
      extractSectionLinks(section.body).forEach((to) => {
        if (!doc.byId[to]) unknownLinks.push({ lang: doc.lang, from: section.id, to });
      });
    });
  });

  return { missingInEn, missingInVi, orderMismatch, unknownLinks };
};

export const isParityValid = (result: PlaybookParityResult): boolean =>
  !result.missingInEn.length && !result.missingInVi.length && !result.orderMismatch && !result.unknownLinks.length;

/** Human readable report of a failed parity check (shown in the panel and logged). */
export const describeParityErrors = (result: PlaybookParityResult): string[] => {
  const lines: string[] = [];
  if (result.missingInEn.length) lines.push(`Missing in EN file: ${result.missingInEn.join(', ')}`);
  if (result.missingInVi.length) lines.push(`Missing in VI file: ${result.missingInVi.join(', ')}`);
  if (result.orderMismatch) lines.push('VI and EN files list the same codes in a different order');
  result.unknownLinks.forEach((l) =>
    lines.push(`[${l.to}] linked from ${l.from} (${l.lang.toUpperCase()}) does not exist`),
  );
  return lines;
};
